// Offerings grouped for the GPU picker (ui-spec §3.5, §8.2): one row per accelerator family with its counts,
// "from" price and availability. The "from" price is the lowest among offerings with a machine free now, as default
// placement takes it, or among all of them when none has one. Offerings are customer-safe capacity classes
// (ADR-011): names, families, vendors and region classes only.
import { parseMicros } from "./format";
import { GPU_CATALOG } from "./gpus.gen";

export interface OfferingLike {
  metadata: { name: string };
  spec?: {
    category?: "GPU" | "CPU";
    accelerator?: { id?: string; family?: string; displayName?: string; memory?: string; vendor?: string };
    count?: number;
    regionClass?: string;
    interruptible?: boolean;
    host?: { cpu?: number | string; memory?: string; disk?: string };
  };
  status?: {
    priceUSDPerHour?: { from?: string; typical?: string };
    availability?: "Available" | "Limited" | "Unavailable";
    availableNow?: number;
    observedTime?: string;
  };
}

export interface GpuFamily {
  family: string;
  /** The family as people read it, such as "NVIDIA RTX 4090 · 24 GB", or "NVIDIA H100 · 80–94 GB" across sizes. */
  name: string;
  vendor?: string;
  memory?: string;
  counts: number[];
  configurations: number;
  /** Lowest per-hour "from" price across the family's offerings with a machine free now (else all), as MoneyUSD. */
  fromUSDPerHour?: string;
  available: "Available" | "Limited" | "Unavailable";
  offerings: string[];
}

/** Whether placement can take an offering now: a machine is free, or stock is known to be nonzero. */
export function offeringInStock(o: OfferingLike): boolean {
  const a = o.status?.availability;
  return a ? a !== "Unavailable" : (o.status?.availableNow ?? 0) > 0;
}

function availability(total: number): GpuFamily["available"] {
  if (total >= 4) return "Available";
  return total > 0 ? "Limited" : "Unavailable";
}

/** A repeated resource is one offering, not additional capacity. The last snapshot wins. */
export function uniqueOfferings(offerings: readonly OfferingLike[]): OfferingLike[] {
  return [...new Map(offerings.map((o) => [o.metadata.name, o])).values()];
}

/** Catalog aliases share a canonical family; unknown hardware keeps its API identity. */
export function offeringFamily(o: OfferingLike): string {
  const a = o.spec?.accelerator;
  for (const token of [a?.id, a?.family, a?.displayName]) {
    if (!token) continue;
    const match = GPU_CATALOG.find((gpu) => [gpu.id, gpu.family, gpu.slug, gpu.displayName, ...gpu.aliases].some((alias) => squash(alias) === squash(token)));
    if (match) return match.family;
  }
  return a?.family ?? o.metadata.name;
}

export function groupOfferings(offerings: readonly OfferingLike[], category: "GPU" | "CPU" = "GPU"): GpuFamily[] {
  const byFamily = new Map<string, OfferingLike[]>();
  for (const o of uniqueOfferings(offerings)) {
    if ((o.spec?.category ?? "GPU") !== category) continue;
    const family = category === "GPU" ? offeringFamily(o) : `${o.spec?.host?.cpu ?? "?"} vCPU`;
    byFamily.set(family, [...(byFamily.get(family) ?? []), o]);
  }
  const out: GpuFamily[] = [];
  for (const [family, list] of byFamily) {
    let best: number | undefined;
    let bestText: string | undefined;
    const stocked = list.filter(offeringInStock);
    for (const o of stocked.length ? stocked : list) {
      const m = parseMicros(o.status?.priceUSDPerHour?.from);
      if (m !== undefined && (best === undefined || m < best)) {
        best = m;
        bestText = o.status?.priceUSDPerHour?.from;
      }
    }
    const first = list[0]!;
    out.push({
      family,
      name: category === "GPU" ? familyName(family, list) : family,
      ...(first.spec?.accelerator?.vendor ? { vendor: first.spec.accelerator.vendor } : {}),
      ...(first.spec?.accelerator?.memory ? { memory: memoryLabel(first.spec.accelerator.memory) } : {}),
      counts: [...new Set(list.map((o) => o.spec?.count ?? 1))].sort((a, b) => a - b),
      configurations: list.length,
      ...(bestText ? { fromUSDPerHour: bestText } : {}),
      available: availability(list.reduce((n, o) => n + (o.status?.availableNow ?? 0), 0)),
      offerings: list.map((o) => o.metadata.name).sort(),
    });
  }
  return out.sort((a, b) => (parseMicros(a.fromUSDPerHour) ?? Infinity) - (parseMicros(b.fromUSDPerHour) ?? Infinity) || a.family.localeCompare(b.family));
}

/** The oldest observedTime: how fresh the prices shown are ("Prices as of 12:04 UTC"). */
export function pricesAsOf(offerings: readonly OfferingLike[]): string | undefined {
  const times = offerings.map((o) => o.status?.observedTime).filter((t): t is string => Boolean(t)).sort();
  return times[0];
}

/** "80Gi" as people read GPU memory: "80 GB". Other quantities pass through. */
export function memoryLabel(q: string): string {
  const m = /^(\d+)Gi$/.exec(q);
  return m ? `${m[1]} GB` : q;
}

/** Several variants' names read as their shared words and memory ("NVIDIA H100 · 80–94 GB"), else joined by "or". */
export function variantsName(names: readonly string[]): string {
  if (names.length === 1) return names[0]!;
  const parts = names.map((n) => /^(.*) · (\d+) GB$/.exec(n));
  if (parts.every(Boolean)) {
    const words = parts.map((m) => m![1]!.split(" "));
    const shared: string[] = [];
    for (let i = 0; words.every((w) => w[i] !== undefined && w[i] === words[0]![i]); i++) shared.push(words[0]![i]!);
    const gb = [...new Set(parts.map((m) => Number(m![2])))].sort((x, y) => x - y);
    if (shared.length >= 2) return `${shared.join(" ")} · ${gb.length === 1 ? gb[0] : `${gb[0]}–${gb[gb.length - 1]}`} GB`;
  }
  return names.join(" or ");
}

function familyName(family: string, list: readonly OfferingLike[]): string {
  const names = [...new Set(list.map((o) => o.spec?.accelerator?.displayName).filter((n): n is string => !!n))];
  if (names.length) return variantsName(names);
  const vendor = list[0]?.spec?.accelerator?.vendor;
  return vendor ? `${vendor} ${family}` : family;
}

function squash(s: string): string {
  return s.toLowerCase().replace(/[\s_-]/g, "");
}

/** Every catalog GPU as an offering-shaped row, so names resolve for GPUs no current offering carries. */
const catalog: OfferingLike[] = GPU_CATALOG.map((a) => ({
  metadata: { name: a.slug },
  spec: { category: "GPU", accelerator: { id: a.id, family: a.family, displayName: a.displayName, memory: a.memory, vendor: a.vendor } },
}));

function nameFrom(t: string, token: string, offerings: readonly OfferingLike[]): string | undefined {
  const byOffering = offerings.find((o) => o.metadata.name === token || squash(o.metadata.name) === t);
  if (byOffering?.spec?.accelerator?.displayName) return byOffering.spec.accelerator.displayName;
  const exact = offerings.find((o) => {
    const a = o.spec?.accelerator;
    return a && (squash(a.id ?? "") === t || (a.displayName && squash(a.displayName) === t));
  });
  if (exact?.spec?.accelerator?.displayName) return exact.spec.accelerator.displayName;
  const fam = offerings.filter((o) => squash(o.spec?.accelerator?.family ?? "") === t);
  if (fam.length) return familyName(fam[0]!.spec!.accelerator!.family!, fam);
  return undefined;
}

/**
 * The display name for a GPU a run asked for or ran on: a family (`H100`), an id (`H100-SXM`), a slug
 * (`h100-sxm-80g`) or an offering name, as the API names it. A family with several variants reads as each
 * variant's name joined by "or". The org's offerings answer first, then the catalog; unknown tokens come back
 * unchanged.
 */
export function gpuName(token: string, offerings: readonly OfferingLike[]): string {
  const t = squash(token.replace(/!$/, ""));
  return nameFrom(t, token, offerings) ?? nameFrom(t, token, catalog) ?? aliases.get(t) ?? token;
}

/** Every other spelling the API accepts for a catalog GPU ("A100-80GB"), squashed, to its display name. */
const aliases = new Map<string, string>(GPU_CATALOG.flatMap((a) => a.aliases.map((s): [string, string] => [squash(s), a.displayName])));
